import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { TextTooltip } from '@ui/components/TextTooltip';
import {
  calculateSpreadPercentage,
  getSpreadPercentageInfo,
} from '@ui/features/Spread/useSpreadPercentage';
import { useTradePairInfos } from '@ui/hooks/trade/useTradePairInfos';
import { Text } from '@ui/lib/park/ui/text';
import {
  ClosePositionDetailExecutionTypeEnum,
  TradeHistoryResponseOperationTypeEnum,
} from '@ui/services/backend/generated';
import { TradeHistory } from '@ui/services/backend/hooks/useTradeHistory/type';
import { getTokenPriceDisplayDecimals } from '@ui/utils/getTokenPriceDisplayDecimals';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'react-i18next';
import { Box, HStack, VStack } from 'styled-system/jsx';

export const HistoryClosePrice = ({ history }: { history: TradeHistory }) => {
  const { t } = useTranslation();
  const tradePairs = useTradePairInfos();
  const tradePair = tradePairs?.find(
    (p) => p.address.toLowerCase() === history.pair.address.toLowerCase(),
  );

  let content:
    | {
        type: 'single';
        label: string;
        price: BigNumber;
        tooltip?: string;
      }
    | { type: 'update'; label: string; from: BigNumber; to: BigNumber }
    | undefined;

  switch (history.type) {
    case TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful:
    case TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful: {
      if (
        history.detail.executionType === ClosePositionDetailExecutionTypeEnum.Tp
      ) {
        content = {
          type: 'single',
          label: 'TP Price',
          price: history.detail.closePrice,
          tooltip: t('The price is after applying slippage'),
        };
      } else if (
        history.detail.executionType === ClosePositionDetailExecutionTypeEnum.Sl
      ) {
        content = {
          type: 'single',
          label: 'SL Price',
          price: history.detail.closePrice,
          tooltip: t('The price is after applying slippage'),
        };
      } else if (
        history.detail.executionType ===
        ClosePositionDetailExecutionTypeEnum.Liq
      ) {
        let liqPrice = history.detail.closePrice;

        if (tradePair) {
          const spreadPercentageInfo = getSpreadPercentageInfo({
            tradePair,
            positionSize: history.size.times(history.detail.closePrice),
            pairPrice: history.detail.closePrice,
          });
          const slippageP = calculateSpreadPercentage({
            isLong: history.isLong,
            isClosePrice: true,
            spreadPercentageInfo,
          });

          if (slippageP) {
            if (history.isLong) {
              liqPrice = liqPrice.div(1 - slippageP);
            } else {
              liqPrice = liqPrice.div(1 + slippageP);
            }
          }
        }

        content = {
          type: 'single',
          label: 'Liq Price',
          price: liqPrice,
        };
      } else if (
        history.detail.executionType ===
        ClosePositionDetailExecutionTypeEnum.Adl
      ) {
        content = {
          type: 'single',
          label: 'ADL Price',
          price: history.detail.closePrice,
          tooltip: t('The price is after applying slippage'),
        };
      }
      break;
    }
    case TradeHistoryResponseOperationTypeEnum.CloseTradeSuccessful:
    case TradeHistoryResponseOperationTypeEnum.ClosePosition:
      content = {
        type: 'single',
        label: 'Close Price',
        price: history.detail.closePrice,
        tooltip: t('The price is after applying slippage'),
      };
      break;
    case TradeHistoryResponseOperationTypeEnum.PositionDecreased:
      content = {
        type: 'single',
        label: 'Decrease Price',
        price: history.detail.closePrice,
        tooltip: t('The price is after applying slippage'),
      };
      break;
    case TradeHistoryResponseOperationTypeEnum.OpenMarketTrade:
    case TradeHistoryResponseOperationTypeEnum.OpenPosition:
      content = {
        type: 'single',
        label: 'Entry Price',
        price: history.detail.atPrice,
      };
      break;
    case TradeHistoryResponseOperationTypeEnum.PositionIncreased:
      content = {
        type: 'single',
        label: 'Increase Price',
        price: history.detail.atPrice,
      };
      break;
    case TradeHistoryResponseOperationTypeEnum.OpenLimitOrder:
      content = {
        type: 'single',
        label: 'Trigger Price',
        price: history.detail.price,
      };
      break;
    case TradeHistoryResponseOperationTypeEnum.DecreaseOrderCreated:
      if (history.detail.triggerPrice) {
        content = {
          type: 'single',
          label: `${history.detail.kind} Trigger`,
          price: history.detail.triggerPrice,
        };
      }
      break;
    case TradeHistoryResponseOperationTypeEnum.DecreaseOrderUpdated:
      if (history.detail.oldTriggerPrice && history.detail.triggerPrice) {
        content = {
          type: 'update',
          label: `Update ${history.detail.kind}`,
          from: history.detail.oldTriggerPrice,
          to: history.detail.triggerPrice,
        };
      } else if (history.detail.triggerPrice) {
        content = {
          type: 'single',
          label: `${history.detail.kind} Trigger`,
          price: history.detail.triggerPrice,
        };
      }
      break;
    case TradeHistoryResponseOperationTypeEnum.UpdateTradeTp:
      content = {
        type: 'update',
        label: 'Update TP',
        from: history.detail.oldTp,
        to: history.detail.tp,
      };
      break;
    case TradeHistoryResponseOperationTypeEnum.UpdateTradeSl:
      content = {
        type: 'update',
        label: 'Update SL',
        from: history.detail.oldSl,
        to: history.detail.sl,
      };
      break;
    case TradeHistoryResponseOperationTypeEnum.UpdateMargin:
      content = {
        type: 'update',
        label: 'Update Margin',
        from: history.detail.previousCollateral,
        to: history.detail.nextCollateral,
      };
      break;
  }

  if (!content) {
    // render an empty grid cell placeholder to keep column alignment
    return <Box />;
  }

  return (
    <VStack alignItems="flex-start" gap={1}>
      {content.type === 'single' && content.tooltip ? (
        <TextTooltip
          content={content.tooltip}
          triggerCss={{
            textDecoration: 'none',
            cursor: 'help',
          }}
        >
          <Text textStyle="v2.meta" color="v2.text.muted">
            {content.label}
          </Text>
        </TextTooltip>
      ) : (
        <Text textStyle="v2.meta" color="v2.text.muted">
          {content.label}
        </Text>
      )}
      {content.type === 'single' ? (
        <SingleValue
          price={content.price}
          priceDisplayDecimals={history.pair.priceDisplayDecimals}
        />
      ) : (
        <UpdateValue
          from={content.from}
          to={content.to}
          priceDisplayDecimals={history.pair.priceDisplayDecimals}
        />
      )}
    </VStack>
  );
};

const SingleValue = ({
  price,
  priceDisplayDecimals,
}: {
  price: BigNumber;
  priceDisplayDecimals?: number;
}) => {
  const displayDecimals = getTokenPriceDisplayDecimals(
    price.toNumber(),
    priceDisplayDecimals,
  );

  return (
    <AmountUsd
      value={price}
      textStyle="numbers.small"
      color="v2.text.secondary"
      opts={{ displayDecimals }}
      data-cy="HistoryClosePrice"
      data-cy-value={price}
    />
  );
};

const UpdateValue = ({
  from,
  to,
  priceDisplayDecimals,
}: {
  from: BigNumber;
  to: BigNumber;
  priceDisplayDecimals?: number;
}) => {
  const displayDecimals = getTokenPriceDisplayDecimals(
    to.toNumber(),
    priceDisplayDecimals,
  );

  return (
    <HStack gap={1} alignItems="center">
      <AmountUsd
        value={from}
        textStyle="numbers.small"
        color="v2.text.subtle"
        opts={{ displayDecimals }}
      />
      <Text textStyle="numbers.small" color="v2.text.subtle">
        →
      </Text>
      <AmountUsd
        value={to}
        textStyle="numbers.small"
        color="v2.text.secondary"
        opts={{ displayDecimals }}
      />
    </HStack>
  );
};
